// packages/sdk/react-client/src/client/ClientContext.tsx
import React, { useState, createContext, useContext, useEffect } from "react";
import { Client, SystemStatus } from "@dxos/client";
import { raise } from "@dxos/debug";
import { log } from "@dxos/log";
import { getAsyncValue } from "@dxos/util";

// packages/sdk/react-client/src/banner.ts
var BANNER = (client) => `
_/\\/\\/\\/\\/\\____/\\/\\____/\\/\\____/\\/\\/\\/\\______/\\/\\/\\/\\/\\_
_/\\/\\____/\\/\\____/\\/\\/\\/\\____/\\/\\____/\\/\\__/\\/\\_________
_/\\/\\____/\\/\\______/\\/\\______/\\/\\____/\\/\\____/\\/\\/\\/\\___
_/\\/\\____/\\/\\____/\\/\\/\\/\\____/\\/\\____/\\/\\__________/\\/\\_
_/\\/\\/\\/\\/\\____/\\/\\____/\\/\\____/\\/\\/\\/\\____/\\/\\/\\/\\/\\___

 DXOS Client ${client.version} 
`;
var bannerPrinted = false;
var printBanner = (client) => {
  if (bannerPrinted) {
    return;
  }
  bannerPrinted = true;
  console.log(BANNER(client));
};

// packages/sdk/react-client/src/client/ClientContext.tsx
var ClientContext = /* @__PURE__ */ createContext(void 0);
var useClient = () => {
  var _a;
  const { client } = (_a = useContext(ClientContext)) != null ? _a : raise(new Error("Missing ClientContext."));
  return client;
};
var ClientProvider = ({ children, config: configProvider, services: createServices, client: clientProvider, fallback: Fallback = () => null, onInitialized }) => {
  const [client, setClient] = useState(clientProvider instanceof Client ? clientProvider : void 0);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState();
  if (error) {
    throw error;
  }
  useEffect(() => {
    if (!client) {
      return;
    }
    const subscription = client.status.subscribe((newStatus) => setStatus(newStatus));
    return () => subscription.unsubscribe();
  }, [
    client,
    setStatus
  ]);
  useEffect(() => {
    const done = async (client2) => {
      var _a;
      log("client ready", {}, {
        file: "ClientContext.tsx",
        line: 104,
        scope: void 0,
        callSite: (f, a) => f(...a)
      });
      await client2.initialize().catch(setError);
      await (onInitialized == null ? void 0 : onInitialized(client2));
      setClient(client2);
      setStatus((_a = client2.status.get()) != null ? _a : SystemStatus.ACTIVE);
      printBanner(client2);
    };
    const timeout = setTimeout(async () => {
      if (clientProvider) {
        const client2 = await getAsyncValue(clientProvider);
        await done(client2);
      } else {
        const config = await getAsyncValue(configProvider);
        log("resolved config", {
          config
        }, {
          file: "ClientContext.tsx",
          line: 120,
          scope: void 0,
          callSite: (f, a) => f(...a)
        });
        const services = createServices == null ? void 0 : createServices(config);
        log("created services", {
          services
        }, {
          file: "ClientContext.tsx",
          line: 122,
          scope: void 0,
          callSite: (f, a) => f(...a)
        });
        const client1 = new Client({
          config,
          services
        });
        log("created client", {}, {
          file: "ClientContext.tsx",
          line: 124,
          scope: void 0,
          callSite: (f, a) => f(...a)
        });
        await done(client1);
      }
    });
    return () => {
      log("clean up", {}, {
        file: "ClientContext.tsx",
        line: 130,
        scope: void 0,
        callSite: (f, a) => f(...a)
      });
      clearTimeout(timeout);
      void (client == null ? void 0 : client.destroy().catch((err) => log.catch(err, {}, {
        file: "ClientContext.tsx",
        line: 132,
        scope: void 0,
        callSite: (f, a) => f(...a)
      })));
    };
  }, [
    clientProvider,
    configProvider,
    createServices
  ]);
  if (!client || status !== SystemStatus.ACTIVE) {
    return /* @__PURE__ */ React.createElement(Fallback, {
      client,
      status
    });
  }
  return /* @__PURE__ */ React.createElement(ClientContext.Provider, {
    value: {
      client,
      status
    }
  }, children);
};

// packages/sdk/react-client/src/client/useClientServices.ts
import { useContext as useContext2 } from "react";
import { raise as raise2 } from "@dxos/debug";
var useClientServices = () => {
  var _a;
  const { services } = (_a = useContext2(ClientContext)) != null ? _a : raise2(new Error("Missing ClientContext."));
  return services;
};

// packages/sdk/react-client/src/client/useConfig.ts
import { useContext as useContext3 } from "react";
import { raise as raise3 } from "@dxos/debug";
var useConfig = () => {
  var _a;
  const { client } = (_a = useContext3(ClientContext)) != null ? _a : raise3(new Error("Missing ClientContext."));
  return client.config;
};

// packages/sdk/react-client/src/client/useDevtools.ts
import assert from "@dxos/node-std/assert";
import { useContext as useContext4 } from "react";
import { raise as raise4 } from "@dxos/debug";
var useDevtools = () => {
  var _a;
  const { services } = (_a = useContext4(ClientContext)) != null ? _a : raise4(new Error("Missing ClientContext."));
  assert(services);
  return services.DevtoolsHost;
};

// packages/sdk/react-client/src/client/useStatus.ts
import { useContext as useContext5 } from "react";
import { raise as raise5 } from "@dxos/debug";
var useStatus = () => {
  var _a;
  const { status } = (_a = useContext5(ClientContext)) != null ? _a : raise5(new Error("Missing ClientContext."));
  return status;
};

// packages/sdk/react-client/src/invitations/useInvitationStatus.ts
import { useReducer, useMemo, useCallback, useEffect as useEffect2 } from "react";
import { Invitation, InvitationEncoder } from "@dxos/client";
import { log as log2 } from "@dxos/log";
var useInvitationStatus = (initialObservable) => {
  const [state, dispatch] = useReducer((prev, action) => {
    log2("useInvitationStatus", {
      action
    }, {
      file: "useInvitationStatus.ts",
      line: 77,
      scope: void 0,
      callSite: (f, a) => f(...a)
    });
    return {
      ...prev,
      status: action.status,
      // `invitationObservable`, `secret`, and `result` is persisted between the status-actions that set them.
      result: action.status === Invitation.State.SUCCESS ? action.result : prev.result,
      // `error` gets set each time we enter the error state
      ...action.status === Invitation.State.ERROR && {
        error: action.error
      },
      // `haltedAt` gets set on only the first error/cancelled/timeout action and reset on any others.
      ...(action.status === Invitation.State.ERROR || action.status === Invitation.State.CANCELLED || action.status === Invitation.State.TIMEOUT) && {
        haltedAt: typeof prev.haltedAt === "undefined" ? action.haltedAt : prev.haltedAt
      }
    };
  }, null, (_arg) => {
    return {
      status: Invitation.State.INIT,
      result: {
        spaceKey: null,
        identityKey: null,
        swarmKey: null
      },
      observable: initialObservable
    };
  });
  useEffect2(() => {
    var _a;
    const subscription = (_a = state.observable) == null ? void 0 : _a.subscribe((invitation) => {
      switch (invitation.state) {
        case Invitation.State.CONNECTED:
        case Invitation.State.READY_FOR_AUTHENTICATION:
        case Invitation.State.AUTHENTICATING: {
          dispatch({
            status: invitation.state
          });
          break;
        }
        case Invitation.State.SUCCESS: {
          dispatch({
            status: invitation.state,
            result: {
              spaceKey: invitation.spaceKey || null,
              identityKey: invitation.identityKey || null,
              swarmKey: invitation.swarmKey || null
            }
          });
          break;
        }
        case Invitation.State.CANCELLED:
        case Invitation.State.TIMEOUT: {
          dispatch({
            status: invitation.state,
            haltedAt: state.status
          });
          break;
        }
      }
    }, (err) => {
      dispatch({
        status: Invitation.State.ERROR,
        error: err,
        haltedAt: state.status
      });
    });
    return () => subscription == null ? void 0 : subscription.unsubscribe();
  }, [
    state.observable,
    state.status
  ]);
  const connect = useCallback((observable) => {
    dispatch({
      status: Invitation.State.CONNECTING,
      observable
    });
  }, []);
  const authenticate = useCallback((authCode) => {
    log2("authenticating...", {
      authCode
    }, {
      file: "useInvitationStatus.ts",
      line: 153,
      scope: void 0,
      callSite: (f, a) => f(...a)
    });
    return state.observable.authenticate(authCode);
  }, [
    state.observable
  ]);
  const cancel = useCallback(async () => {
    var _a;
    return (_a = state.observable) == null ? void 0 : _a.cancel();
  }, [
    state.observable
  ]);
  return useMemo(() => {
    var _a;
    const invitation = (_a = state.observable) == null ? void 0 : _a.get();
    const result = {
      status: state.status,
      haltedAt: state.haltedAt,
      result: state.result,
      error: state.error,
      cancel,
      connect,
      authenticate,
      id: invitation == null ? void 0 : invitation.invitationId,
      invitationCode: invitation ? InvitationEncoder.encode(invitation) : void 0,
      authCode: invitation == null ? void 0 : invitation.authCode,
      authMethod: invitation == null ? void 0 : invitation.authMethod
    };
    if (result.status === Invitation.State.READY_FOR_AUTHENTICATION && result.authCode) {
      log2.info(JSON.stringify({
        authCode: result.authCode
      }), {}, {
        file: "useInvitationStatus.ts",
        line: 180,
        scope: void 0,
        callSite: (f, a) => f(...a)
      });
    } else if (result.status === Invitation.State.INIT && result.invitationCode) {
      log2.info(JSON.stringify({
        invitationCode: result.invitationCode
      }), {}, {
        file: "useInvitationStatus.ts",
        line: 182,
        scope: void 0,
        callSite: (f, a) => f(...a)
      });
    }
    return result;
  }, [
    state,
    cancel,
    connect,
    authenticate
  ]);
};

// packages/sdk/react-client/src/halo/useContacts.ts
import { useSyncExternalStore } from "react";
var useContacts = () => {
  const client = useClient();
  const contacts = useSyncExternalStore((listener) => {
    const subscription = client.halo.contacts.subscribe(listener);
    return () => subscription.unsubscribe();
  }, () => client.halo.contacts.get());
  return contacts;
};

// packages/sdk/react-client/src/halo/useDevices.ts
import { useSyncExternalStore as useSyncExternalStore2 } from "react";
var useDevices = () => {
  const client = useClient();
  const devices = useSyncExternalStore2((listener) => {
    const subscription = client.halo.devices.subscribe(listener);
    return () => subscription.unsubscribe();
  }, () => client.halo.devices.get());
  return devices;
};

// packages/sdk/react-client/src/halo/useIdentity.ts
import { useEffect as useEffect3, useSyncExternalStore as useSyncExternalStore3 } from "react";
import { IFrameClientServicesProxy, ShellLayout } from "@dxos/client";
var useIdentity = (options) => {
  const { login } = {
    login: false,
    ...options
  };
  const client = useClient();
  const identity = useSyncExternalStore3((listener) => {
    const subscription = client.halo.identity.subscribe(listener);
    return () => subscription.unsubscribe();
  }, () => client.halo.identity.get());
  useEffect3(() => {
    if (login && !identity && client.services instanceof IFrameClientServicesProxy) {
      void client.services.setLayout(ShellLayout.INITIALIZE_IDENTITY);
    }
  }, [
    client,
    identity,
    login
  ]);
  return identity;
};

// packages/sdk/react-client/src/halo/useHaloInvitations.ts
import { useMemo as useMemo2, useSyncExternalStore as useSyncExternalStore4 } from "react";
var useHaloInvitations = () => {
  var _a;
  const client = useClient();
  const invitations = (_a = useSyncExternalStore4((listener) => {
    const subscription = client.halo.invitations.subscribe(() => listener());
    return () => subscription.unsubscribe();
  }, () => client.halo.invitations.get())) != null ? _a : [];
  return invitations;
};
var useHaloInvitation = (invitationId) => {
  const invitations = useHaloInvitations();
  const invitation = useMemo2(() => invitations.find((invitation2) => invitation2.get().invitationId === invitationId), [
    invitations
  ]);
  return useInvitationStatus(invitation);
};

export {
  ClientContext,
  useClient,
  ClientProvider,
  useClientServices,
  useConfig,
  useDevtools,
  useStatus,
  useInvitationStatus,
  useContacts,
  useDevices,
  useIdentity,
  useHaloInvitations,
  useHaloInvitation
};
//# sourceMappingURL=chunk-4CBWGYQ7.mjs.map
